import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  Fab, Dialog, Typography, Box, Chip, IconButton, Button, TextField,
  Select, MenuItem, FormControl, Paper, Tooltip, CircularProgress,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Remove";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { authFetch } from "../utils/authHelpers";
import { useAssistant } from "../contexts/AssistantContext";
import { BACKEND_URL } from "../utils/apiConfig";
import { getToolNames, resolveToolName } from "../config/navigation";

/**
 * Feedback in één scherm (Joris, 6 sep: "feedback wordt niet echt gebruikt —
 * gebruiksvriendelijker maken"). De oude chat-flow (project → tool → type →
 * vervolgvragen → screenshot → bevestigen) stelde te veel vragen; nu: pagina
 * staat al goed, één tekstvak, type raden we (aanpasbaar), screenshot plakken,
 * versturen. Zelfde API-contract als voorheen (/Api/feedback).
 */

const TOOL_NAMES = getToolNames();

const WEBSITE_PAGES = {
  "/": "Home", "/propositions": "Propositions", "/work": "Work",
  "/approach": "Approach", "/approach/formula": "Approach / Formula",
  "/ai": "AI", "/insights": "Insights", "/about": "About",
  "/contact": "Contact", "/privacy": "Privacy",
  "/algemene-voorwaarden": "Algemene Voorwaarden",
};

const getToolName = (pathname, targetRepo) => {
  if (targetRepo === "websiteff") return WEBSITE_PAGES[pathname] || "Website pagina";
  return resolveToolName(pathname) || "Onbekende pagina";
};

const TYPES = [
  { value: "bug", label: "Werkt niet", icon: <BugReportIcon sx={{ fontSize: 16 }} />, color: "#dc2626" },
  { value: "improvement", label: "Kan beter", icon: <TuneIcon sx={{ fontSize: 16 }} />, color: "#4f46e5" },
  { value: "feature", label: "Idee", icon: <LightbulbIcon sx={{ fontSize: 16 }} />, color: "#d97706" },
];

// Geen \b aan het eind: "crasht", "fouten", "mislukte" tellen ook mee.
const BUG_WORDS = /\b(bug|fout|crash|error|werkt niet|kapot|stuk|broken|vastl|mislukt|500|404|laadt niet|loopt vast|wit scherm|leeg|doet het niet|verdwenen|klopt niet|springt terug)/i;
const FEATURE_WORDS = /\b(wil|graag|nieuw|toevoegen|kunnen|zou.*mooi|feature|idee|optie|knop|pagina|functie|integratie|koppeling|zou fijn)\b/i;
function detectType(text) {
  if (BUG_WORDS.test(text)) return "bug";
  if (FEATURE_WORDS.test(text)) return "feature";
  return "improvement";
}

const MAX_SCREENSHOTS = 5;
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/** Gedeelde stijl voor de zwevende knoppen rechtsonder (feedback + assistent). */
export const DOCK_FAB_SX = {
  position: "fixed", right: 24, zIndex: 1200, width: 46, height: 46,
  bgcolor: "#0f172a", color: "#fff", boxShadow: "0 8px 24px rgba(15,23,42,0.28)",
  border: "1px solid rgba(255,255,255,0.08)",
  "&:hover": { bgcolor: "#1e293b", transform: "translateY(-1px)" },
  transition: "transform .15s, background-color .15s",
};

const LABEL_SX = { fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8", mb: 0.75 };

export default function FeedbackFAB() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const location = useLocation();
  const detectedRoute = location.pathname;
  const { isOpen: assistantOpen } = useAssistant();

  const [targetRepo, setTargetRepo] = useState("ff-material-app");
  const [selectedRoute, setSelectedRoute] = useState(detectedRoute);
  const [description, setDescription] = useState("");
  const [manualType, setManualType] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const feedbackType = manualType || (description.trim() ? detectType(description) : null);
  const submitted = submittedId !== null;

  useEffect(() => { if (open && !minimized && !submitted) setTimeout(() => inputRef.current?.focus(), 250); }, [open, minimized, submitted]);
  useEffect(() => { setSelectedRoute(detectedRoute); }, [detectedRoute]);
  useEffect(() => { if (detectedRoute === "/website-preview") setTargetRepo("websiteff"); }, [detectedRoute]);

  // Andere tools kunnen het venster openen (bv. Website Preview met een pagina voorgeselecteerd).
  useEffect(() => {
    const handler = (e) => {
      const { targetRepo: repo, page, route: appRoute } = e.detail || {};
      if (repo) setTargetRepo(repo);
      if (page) setSelectedRoute(Object.entries(WEBSITE_PAGES).find(([, name]) => name === page)?.[0] || "/");
      if (appRoute) setSelectedRoute(appRoute);
      setMinimized(false); setOpen(true);
    };
    window.addEventListener("open-feedback", handler);
    return () => window.removeEventListener("open-feedback", handler);
  }, []);

  const addScreenshotsFromFiles = useCallback(async (files) => {
    const remaining = MAX_SCREENSHOTS - screenshots.length;
    const nieuw = [];
    for (const file of Array.from(files).slice(0, Math.max(0, remaining))) {
      if (!file.type.startsWith("image/")) continue;
      nieuw.push({ preview: await fileToBase64(file), contentType: file.type });
    }
    if (nieuw.length) setScreenshots((prev) => [...prev, ...nieuw].slice(0, MAX_SCREENSHOTS));
  }, [screenshots.length]);

  // Plakken werkt zolang het venster open is — je hoeft geen aparte stap te bereiken.
  useEffect(() => {
    if (!open || minimized || submitted) return undefined;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items; if (!items) return;
      const imgs = [];
      for (const item of items) if (item.type.startsWith("image/")) { const b = item.getAsFile(); if (b) imgs.push(b); }
      if (imgs.length) { e.preventDefault(); addScreenshotsFromFiles(imgs); }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [open, minimized, submitted, addScreenshotsFromFiles]);

  const toolName = useMemo(() => getToolName(selectedRoute, targetRepo), [selectedRoute, targetRepo]);
  const toolOptions = useMemo(() => {
    const source = targetRepo === "websiteff" ? WEBSITE_PAGES : TOOL_NAMES;
    const opts = Object.entries(source).map(([route, name]) => ({ route, name })).sort((a, b) => a.name.localeCompare(b.name));
    if (!opts.some((o) => o.route === selectedRoute)) opts.unshift({ route: selectedRoute, name: toolName });
    return opts;
  }, [targetRepo, selectedRoute, toolName]);

  const reset = () => {
    setTargetRepo(detectedRoute === "/website-preview" ? "websiteff" : "ff-material-app");
    setSelectedRoute(detectedRoute); setDescription(""); setManualType(null); setScreenshots([]);
    setError(null); setSubmittedId(null); setPhoneNumber(""); setPhoneSaved(false); setMinimized(false); setDragOver(false);
  };
  const handleClose = () => { setOpen(false); setTimeout(reset, 300); };

  const handleSubmit = async () => {
    if (!description.trim() || submitting) return;
    setSubmitting(true); setError(null);
    try {
      let screenshotUrls = null;
      if (screenshots.length) {
        try {
          const up = await authFetch(`${BACKEND_URL}/Api/feedback/upload-screenshots`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ screenshots: screenshots.map((s) => ({ data: s.preview, content_type: s.contentType })) }),
          });
          if (up.ok) screenshotUrls = (await up.json()).urls;
        } catch (e) { console.warn("[Feedback] Screenshot upload failed:", e); }
      }
      const res = await authFetch(`${BACKEND_URL}/Api/feedback`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool_name: toolName, tool_route: selectedRoute, feedback_type: feedbackType || "improvement",
          description: description.trim(), follow_up_answers: {}, priority: "medium",
          screenshot_urls: screenshotUrls, target_repo: targetRepo, phone_number: null,
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error(`[Feedback] Submit failed: ${res.status}`, body);
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      }
      let id = 0;
      try { id = (await res.json()).id ?? 0; } catch { /* ok */ }
      setSubmittedId(id);
    } catch (err) {
      setError(err.message?.includes("401") ? "Je sessie is verlopen. Herlaad de pagina en probeer opnieuw."
        : /HTTP 5/.test(err.message || "") ? "Er ging iets mis op de server. Probeer het zo nog eens."
        : "Kon de feedback niet opslaan. Probeer het zo nog eens.");
    } finally { setSubmitting(false); }
  };

  const bewaarTelefoon = async () => {
    if (!phoneNumber.trim() || !submittedId) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/feedback/${submittedId}/phone`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone_number: phoneNumber }),
      });
      setPhoneSaved(true);
    } catch { setPhoneSaved(true); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) addScreenshotsFromFiles(e.dataTransfer.files); };

  return (
    <>
      {assistantOpen ? null : minimized ? (
        <Paper onClick={() => setMinimized(false)} elevation={4}
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300, cursor: "pointer", display: "flex", alignItems: "center",
            gap: 1, px: 2, py: 1, borderRadius: 20, bgcolor: "#0f172a", color: "#fff", "&:hover": { bgcolor: "#1e293b" } }}>
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>Feedback — plak je screenshot en klik hier</Typography>
          <OpenInFullIcon sx={{ fontSize: 16 }} />
        </Paper>
      ) : (
        <Tooltip title="Feedback geven" placement="left">
          <Fab onClick={() => setOpen(true)} aria-label="Feedback geven" sx={{ ...DOCK_FAB_SX, bottom: 24 }}>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 21 }} />
          </Fab>
        </Tooltip>
      )}

      <Dialog open={open && !minimized} onClose={() => (submitted ? handleClose() : setMinimized(true))} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: "hidden", m: { xs: 0, sm: undefined }, width: { xs: "100%", sm: undefined } } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.2 }}>Feedback</Typography>
            <Typography variant="caption" color="text.secondary">Eén zin is genoeg. Screenshot erbij is goud.</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {!submitted && (
              <Tooltip title="Even wegklikken om een screenshot te maken — je tekst blijft staan">
                <IconButton onClick={() => setMinimized(true)} size="small"><MinimizeIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </Box>

        {submitted ? (
          <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 44, color: "#10b981", mb: 1 }} />
            <Typography sx={{ fontWeight: 700 }}>Ontvangen, dank je!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Je feedback over <b>{toolName}</b> staat bij het team. Je ziet de voortgang onder Mijn Feedback.
            </Typography>
            {!phoneSaved ? (
              <Box sx={{ mt: 2.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, textAlign: "left" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Een WhatsApp'je als het opgelost is? <span style={{ color: "#94a3b8" }}>(optioneel)</span></Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField size="small" placeholder="06-12345678" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} sx={{ width: 170 }}
                    onKeyDown={(e) => { if (e.key === "Enter") bewaarTelefoon(); }} />
                  <Button size="small" variant="outlined" disabled={!phoneNumber.trim()} onClick={bewaarTelefoon} sx={{ textTransform: "none", fontWeight: 700 }}>Ja, graag</Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>📱 We sturen je een berichtje zodra het live staat.</Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2.5 }}>
              <Button component={RouterLink} to="/my-feedback" onClick={handleClose} size="small" sx={{ textTransform: "none", fontWeight: 700 }}>Mijn Feedback →</Button>
              <Button onClick={handleClose} variant="contained" size="small" disableElevation sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}>Sluiten</Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography sx={LABEL_SX}>Gaat over</Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  {(targetRepo === "websiteff" || detectedRoute === "/website-preview") && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {[["ff-material-app", "Interne app"], ["websiteff", "Website"]].map(([v, l]) => (
                        <Chip key={v} label={l} size="small" onClick={() => { setTargetRepo(v); setSelectedRoute(v === "websiteff" ? "/" : detectedRoute); }}
                          variant={targetRepo === v ? "filled" : "outlined"} sx={{ fontWeight: 700 }} />
                      ))}
                    </Box>
                  )}
                  <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                    <Select value={selectedRoute} onChange={(e) => setSelectedRoute(e.target.value)} sx={{ fontSize: 14, fontWeight: 600 }}>
                      {toolOptions.map((o) => <MenuItem key={o.route} value={o.route} sx={{ fontSize: 14 }}>{o.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <TextField inputRef={inputRef} multiline minRows={4} maxRows={10} fullWidth value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit(); }}
                placeholder={"Wat viel je op, wat ging mis of wat zou je willen?\nBijv. \"Als ik een taak versleep springt hij terug\" of \"Zoekveld bij de takenlijst zou fijn zijn\"."}
                inputProps={{ style: { fontSize: 15, lineHeight: 1.5 } }} />

              <Box>
                <Typography sx={LABEL_SX}>Soort {feedbackType && !manualType ? <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>· automatisch geraden, klik om te wijzigen</span> : null}</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {TYPES.map((t) => {
                    const actief = feedbackType === t.value;
                    return (
                      <Chip key={t.value} icon={t.icon} label={t.label} onClick={() => setManualType(t.value)} variant={actief ? "filled" : "outlined"}
                        sx={{ fontWeight: 700, borderColor: actief ? t.color : "#e2e8f0", bgcolor: actief ? `${t.color}14` : "transparent", color: actief ? t.color : "#475569",
                          "& .MuiChip-icon": { color: actief ? t.color : "#94a3b8" }, "&:hover": { bgcolor: `${t.color}14` } }} />
                    );
                  })}
                </Box>
              </Box>

              <Box>
                <Typography sx={LABEL_SX}>Screenshot <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>· optioneel, max {MAX_SCREENSHOTS}</span></Typography>
                <Box onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ border: `1.5px dashed ${dragOver ? "#4f46e5" : "#cbd5e1"}`, bgcolor: dragOver ? "#eef2ff" : "#f8fafc", borderRadius: 2, p: 1.5, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minHeight: 56 }}>
                  {screenshots.map((s, i) => (
                    <Box key={i} sx={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
                      <img src={s.preview} alt={`screenshot ${i + 1}`} style={{ height: 64, borderRadius: 6, border: "1px solid #e2e8f0", display: "block" }} />
                      <IconButton size="small" onClick={() => setScreenshots((prev) => prev.filter((_, j) => j !== i))}
                        sx={{ position: "absolute", top: -8, right: -8, bgcolor: "#fff", boxShadow: 1, p: 0.25, "&:hover": { bgcolor: "#fee2e2" } }}>
                        <DeleteIcon sx={{ fontSize: 14, color: "#dc2626" }} />
                      </IconButton>
                    </Box>
                  ))}
                  {screenshots.length < MAX_SCREENSHOTS && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
                      <AddPhotoAlternateIcon sx={{ fontSize: 22 }} />
                      <Typography variant="body2">
                        {screenshots.length ? "Nog een: " : ""}plak met <b>Ctrl+V</b>, sleep hierheen of klik om te kiezen
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addScreenshotsFromFiles(e.target.files || []); e.target.value = ""; }} />
              </Box>

              {error && <Typography variant="body2" sx={{ color: "#b91c1c", bgcolor: "#fef2f2", p: 1.25, borderRadius: 1.5 }}>{error}</Typography>}
            </Box>

            <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Komt bij het team terecht met je naam en deze pagina. Ctrl+Enter verstuurt.</Typography>
              <Button onClick={handleSubmit} variant="contained" disableElevation disabled={!description.trim() || submitting}
                endIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: "none", fontWeight: 800, borderRadius: 20, px: 2.5, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}>
                Versturen
              </Button>
            </Box>
          </>
        )}
      </Dialog>
    </>
  );
}
